import React, { useState, useEffect } from 'react';
import { PLAYER_COLORS } from '../shared/constants';
import MapSelector from './MapSelector';

// Component for displaying player sprites in lobby with robust loading
const LobbyPlayerSprite = ({ playerColor, playerSlot, svgSpritesLoaded, characterSpriteSVGs, coloredSpritesCache, createColoredSprite }) => {
  const [spriteState, setSpriteState] = useState({ ready: false, error: false });

  // Use useEffect to handle sprite loading to avoid infinite re-renders
  useEffect(() => {
    if (!svgSpritesLoaded || !characterSpriteSVGs?.front?.[0]) {
      return;
    }

    const cacheKey = `${playerColor}-front-0`;
    let sprite = coloredSpritesCache.current.get(cacheKey);
    
    if (!sprite) {
      try {
        sprite = createColoredSprite(characterSpriteSVGs.front[0], playerColor);
        if (sprite) {
          coloredSpritesCache.current.set(cacheKey, sprite);
          
          if (sprite.complete) {
            setSpriteState({ ready: true, error: false });
          } else {
            // Wait for image to fully load
            sprite.onload = () => {
              setSpriteState({ ready: true, error: false });
            };
            
            sprite.onerror = () => {
              setSpriteState({ ready: false, error: true });
            };
          }
        } else {
          setSpriteState({ ready: false, error: true });
        }
      } catch (error) {
        setSpriteState({ ready: false, error: true });
      }
    } else if (sprite.complete) {
      setSpriteState({ ready: true, error: false });
    }
  }, [svgSpritesLoaded, characterSpriteSVGs, playerColor, playerSlot, coloredSpritesCache, createColoredSprite]);

  // Show loading state while SVG sprites are loading
  if (!svgSpritesLoaded) {
    return (
      <div style={{
        width: '40px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${playerColor}`,
        borderRadius: '4px',
        fontSize: '10px',
        color: '#888'
      }}>
        ...
      </div>
    );
  }

  // Get sprite from cache
  const cacheKey = `${playerColor}-front-0`;
  const sprite = coloredSpritesCache.current.get(cacheKey);

  // Show sprite if ready and loaded
  if (sprite && spriteState.ready) {
    return (
      <img 
        src={sprite.src} 
        alt={`Player ${playerSlot + 1} character`}
        style={{
          width: '32px',
          height: '48px',
          imageRendering: 'pixelated',
          filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))'
        }}
      />
    );
  }

  // Fallback to colored circle
  return (
    <div style={{
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: playerColor,
      border: `2px solid ${playerColor}`,
      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
      marginTop: '8px' // Center vertically with 48px sprite height
    }} />
  );
};

const GameLobby = ({
  players,
  observers,
  myPlayerId,
  myPlayer,
  isObserver,
  audioEnabled,
  lobbyCountdown,
  svgSpritesLoaded,
  characterSpriteSVGs,
  coloredSpritesCache,
  createColoredSprite,
  toggleReady,
  joinAsPlayer,
  becomeObserver,
  copyRoomUrl,
  enableAudio
}) => {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative'
    }}>
      {/* Observers at the top */}
      {observers.length > 0 && (
        <div style={{ 
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          justifyContent: 'center'
        }}>
          <span style={{ color: '#888', fontSize: '14px', marginRight: '10px' }}>Observers:</span>
          {observers.map(observer => (
            <div key={observer.id} style={{ 
              padding: '4px 12px', 
              backgroundColor: '#2a2a2a', 
              border: '1px solid #444',
              color: '#aaa', 
              borderRadius: '15px', 
              fontSize: '12px'
            }}>
              {observer.name}
            </div>
          ))}
        </div>
      )}

      {isObserver && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#1a1a1a', 
          border: '1px solid #333',
          borderRadius: '5px' 
        }}>
          <p><strong>You are observing this game.</strong></p>
          {players.size < 8 && (
            <button 
              onClick={joinAsPlayer}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Join as Player
            </button>
          )}
          {players.size >= 8 && <p>All player slots are full. You'll need to wait for a spot to open up.</p>}
        </div>
      )}

      {/* Map Selection */}
      <MapSelector />
      
      {!isObserver && (
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={copyRoomUrl} style={{ 
            padding: '8px 16px',
            fontSize: '14px',
            backgroundColor: '#4a9eff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}>
            Copy Room URL
          </button>
          <button 
            onClick={becomeObserver}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Become Observer
          </button>
        </div>
      )}
      
      {/* Compact Player List */}
      <div style={{
        marginBottom: '30px',
        width: '100%',
        maxWidth: '600px'
      }}>
        {Array.from(players.values())
          .sort((a, b) => a.playerId - b.playerId) // Sort by player ID
          .map(player => {
            const playerColor = PLAYER_COLORS[player.playerId];
            
            return (
            <div 
              key={player.playerId} 
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                marginBottom: '8px',
                border: `2px solid ${playerColor}`,
                borderRadius: '8px',
                backgroundColor: `${playerColor}15`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: 1
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = `${playerColor}25`;
                e.target.style.boxShadow = `0 0 10px ${playerColor}44`;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = `${playerColor}15`;
                e.target.style.boxShadow = 'none';
              }}
            >
              {/* Player Sprite */}
              <div style={{ 
                marginRight: '16px',
                flexShrink: 0
              }}>
                <LobbyPlayerSprite 
                  playerColor={playerColor} 
                  playerSlot={player.playerId}
                  svgSpritesLoaded={svgSpritesLoaded}
                  characterSpriteSVGs={characterSpriteSVGs}
                  coloredSpritesCache={coloredSpritesCache}
                  createColoredSprite={createColoredSprite}
                />
              </div>
              
              {/* Player Info */}
              <div style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: playerColor,
                  marginBottom: '4px'
                }}>
                  {player.nickname || `Player ${player.playerId + 1}`}
                  {player?.playerId === myPlayerId && (
                    <span style={{
                      fontSize: '12px',
                      color: '#aaa',
                      fontWeight: 'normal',
                      marginLeft: '8px'
                    }}>
                      (You)
                    </span>
                  )}
                </div>
                
                <div style={{
                  fontSize: '14px',
                  color: player.ready ? '#4caf50' : '#888',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  {player.ready ? '✓' : '○'} {player.ready ? 'Ready' : 'Not Ready'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ready/Observer buttons */}
      {!isObserver && players.size >= 2 && (
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={async () => {
              if (!audioEnabled) {
                await enableAudio();
              }
              toggleReady();
            }}
            style={{
              padding: '15px 30px',
              fontSize: '18px',
              backgroundColor: myPlayer?.ready ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              margin: '5px'
            }}
          >
            {myPlayer?.ready ? 'Cancel Ready' : 'Ready Up!'}
          </button>
          <button 
            onClick={becomeObserver}
            style={{
              padding: '15px 30px',
              fontSize: '18px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              margin: '5px'
            }}
          >
            Become Observer
          </button>
        </div>
      )}

      {/* Prominent Countdown Overlay */}
      {lobbyCountdown > 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          border: '4px solid #ff6b6b',
          borderRadius: '20px',
          padding: '40px 60px',
          textAlign: 'center',
          boxShadow: '0 0 30px rgba(255, 107, 107, 0.5), inset 0 0 20px rgba(255, 107, 107, 0.1)'
        }}>
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: '#ff6b6b',
            marginBottom: '20px',
            textShadow: '0 0 20px rgba(255, 107, 107, 0.8)'
          }}>
            {lobbyCountdown}
          </div>
          <div style={{
            fontSize: '24px',
            color: '#fff',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            Game Starting
          </div>
        </div>
      )}
    </div>
  );
};

export default GameLobby;